import React, { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import {
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  useBreakpointValue,
} from "@chakra-ui/react";

import { CloseIcon, SearchIcon } from "icons";
import { inputSize } from "util/styleHelpers";

export type SearchBoxProps = {
  value?: string;
  onChange?(searchTerm: string): void;
};

const SearchBoxWithIcon = ({
  value = "",
  onChange: _onChange = () => {
    return;
  },
}: SearchBoxProps): React.ReactElement => {
  const size = useBreakpointValue(inputSize, { ssr: false });
  const hasText = (value?.length ?? 0) > 0;

  const [localValue, setLocalValue] = useState("");
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const onChangeDebounced = useMemo(
    () => debounce((value: string) => _onChange(value), 300),
    [_onChange]
  );

  const onChange = useCallback(
    (value: string) => {
      setLocalValue(value);
      onChangeDebounced(value);
    },
    [onChangeDebounced]
  );

  return (
    <InputGroup size={size}>
      <Input
        data-testid="searchBox"
        placeholder="Search"
        aria-label="Search box"
        value={localValue}
        onChange={(event) => onChange(event.target.value)}
        maxLength={100}
      />
      <InputRightElement
        zIndex={1}
        pointerEvents={hasText ? "initial" : "none"}
      >
        {hasText ? (
          <IconButton
            aria-label="Clear search"
            variant="link"
            icon={<CloseIcon color="grey.500" />}
            onClick={() => onChange("")}
          />
        ) : (
          <SearchIcon />
        )}
      </InputRightElement>
    </InputGroup>
  );
};

export default SearchBoxWithIcon;
